import { Component, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";

import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { TylerSmartMeterNotificationInfo } from "@tylertech/tyler-api-types";

import { TylerSmartMeterService } from "../../services";

export interface TylerSmartMeterTextEmailDialogData
{
    readonly accountNumber: string;
    readonly alertId: string;
    readonly email: string;
    readonly landlordAccountNumber: string;
    readonly landlordEmail: string;
    readonly landlordPhone: string;
    readonly landlordSms: string;
    readonly phone: string;
    readonly sms: string;
}

@Component({
    selector: "tyler-smart-meter-text-email-dialog-component",
    templateUrl: "./tylerSmartMeterTextEmailDialog.tmpl.html",
    standalone: false
})
export class TylerSmartMeterTextEmailDialogComponent
{
    private readonly dialogRef = inject<MatDialogRef<TylerSmartMeterTextEmailDialogComponent>>(MatDialogRef);
    private readonly data = inject<TylerSmartMeterTextEmailDialogData>(MAT_DIALOG_DATA);
    private commonDialogsService = inject(CommonDialogsService);
    private readonly $tylerSmartMeterService = inject(TylerSmartMeterService);

    readonly formGroup: FormGroup;

    constructor()
    {
        this.formGroup = new FormGroup({
            email: LgdFormControlFactory.checkbox(),
            sms: LgdFormControlFactory.checkbox(),
            phone: LgdFormControlFactory.checkbox(),
            landlordEmail: LgdFormControlFactory.checkbox(),
            landlordSms: LgdFormControlFactory.checkbox(),
            landlordPhone: LgdFormControlFactory.checkbox(),
        });
    }

    private get alertId(): string { return this.data.alertId; }
    private get accountNumber(): string { return this.data.accountNumber; }
    get email(): string { return this.data.email; }
    get sms(): string { return this.data.sms; }
    get phone(): string { return this.data.phone; }
    private get landlordAccountNumber(): string { return this.data.landlordAccountNumber; }
    get landlordEmail(): string { return this.data.landlordEmail; }
    get landlordPhone(): string { return this.data.landlordPhone; }
    get landlordSms(): string { return this.data.landlordSms; }

    private get emailControl(): AbstractControl<boolean> { return this.formGroup.get("email"); }
    private get smsControl(): AbstractControl<boolean> { return this.formGroup.get("sms"); }
    private get phoneControl(): AbstractControl<boolean> { return this.formGroup.get("phone"); }
    private get landlordEmailControl(): AbstractControl<boolean> { return this.formGroup.get("landlordEmail"); }
    private get landlordSmsControl(): AbstractControl<boolean> { return this.formGroup.get("landlordSms"); }
    private get landlordPhoneControl(): AbstractControl<boolean> { return this.formGroup.get("landlordPhone"); }

    get noContactInformation(): boolean { return !this.email && !this.phone && !this.sms; }
    get noLandLordContactInformation(): boolean { return !this.landlordEmail && !this.landlordPhone && !this.landlordSms; }

    get emailLabel(): string { return `Email : ${this.email}`; }
    get smsLabel(): string { return `SMS : ${this.sms}`; }
    get phoneLabel(): string { return `Phone : ${this.phone}`; }

    get landlordEmailLabel(): string { return `Landlord Email : ${this.landlordEmail}`; }
    get landlordSmsLabel(): string { return `Landlord SMS : ${this.landlordSms}`; }
    get landlordPhoneLabel(): string { return `Landlord Phone : ${this.landlordPhone}`; }

    private getLandlordValue(): string
    {
        let value: number = 0;
        if (this.landlordPhoneControl.value)
        {
            value += 1;
        }
        if (this.landlordSmsControl.value)
        {
            value += 2;
        }
        if (this.landlordEmailControl.value)
        {
            value += 4;
        }
        return value.toString();
    }

    private getValue(): string
    {
        let value: number = 0;
        if (this.phoneControl.value)
        {
            value += 1;
        }
        if (this.smsControl.value)
        {
            value += 2;
        }
        if (this.emailControl.value)
        {
            value += 4;
        }
        return value.toString();
    }

    private processResult(title: string, message: string): void
    {
        // TODO - We are already in a dialog context... DO NOT OPEN SECONDARY DIALOGS
        this.commonDialogsService.showAlertHtmlMessage(message, title, "OK");
    }

    sendNotification(): void
    {
        const notificationInfo = <TylerSmartMeterNotificationInfo>{
            AlertProcessingResultId: this.alertId,
            NotificationType: this.getValue(),
            AccountNumber: this.accountNumber,
            LandlordAccountNumber: this.landlordAccountNumber,
            LandlordNotificationType: this.getLandlordValue(),
        };
        this.$tylerSmartMeterService.SendNotification(notificationInfo)
            .then(result =>
            {
                if (result.Success)
                {
                    this.processResult("Success", result.Result);
                }
                else
                {
                    this.processResult("Error", result.ErrorMessage);
                }
            })
            .finally(() =>
            {
                this.dialogRef.close(true);
            });
    }
}
